import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';

import ErrorContext from 'contexts/errorContext';
import { LocalStorageKeys } from 'localStorageKeys';
import StoreContext from 'contexts/storeContext';
import TemporalLabel from 'common/TemporalLabel';
import { WEEKDAYS } from 'consts';
import { makeStyles } from 'tss-react/mui';
import { Tag } from 'types/tag';
import { APIError, FieldErrors, FieldsAPIError } from 'errors';
import { Pack, PackBeingCreated } from 'types/pack';
import {
  ensureSurpriseNamePrefix,
  removeSurpriseNamePrefix,
  SURPRISE_NAME_PREFIX_LABEL,
  SURPRISE_NAME_PREFIX_WITH_SPACE,
} from 'surprisePack';
import BasePackDialogFields from './BasePackDialogFields';

const useStyles = makeStyles()((theme) => ({
  field: {
    paddingBottom: theme.spacing(2),
  },
}));

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  packToEdit?: Pack;
  initialIsSurprise?: boolean;
  callback: (pack: PackBeingCreated) => Promise<any>;
};

export default function PackDialog({ isOpen, onClose, onSuccess, packToEdit, initialIsSurprise = false, callback }: Props) {
  const { store } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);

  const { classes } = useStyles();
  const [pack, setPack] = useState<PackBeingCreated>({});
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [autoQuantityEnabled, setAutoQuantityEnabled] = useState(false);
  const [showAutoPublishLabel, setShowAutoPublishLabel] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (packToEdit) {
      let packCopy: PackBeingCreated = {
        ...packToEdit,
        tags: packToEdit.tags.map((tag: Tag) => tag.id),
        image: packToEdit.image?.id,
      };
      if (packCopy.is_surprise) packCopy.name = removeSurpriseNamePrefix(packCopy.name);
      delete packCopy.quantity;
      setPack(packCopy);
      setAutoQuantityEnabled(!!packToEdit.auto_publish_qty);
      setIsEdit(true);
    } else {
      setPack({ tax_type: store.default_tax_type, is_surprise: initialIsSurprise });
      setAutoQuantityEnabled(false);
      setIsEdit(false);
    }
  }, [packToEdit, store, initialIsSurprise, isOpen]);

  const allowsProlongedPacks = !!store?.allows_prolonged_packs;
  const noDailyReset = !!pack.no_daily_reset;

  useEffect(() => {
    if (isOpen) {
      if (!localStorage.getItem(LocalStorageKeys.HAS_SEEN_AUTO_PUBLISH)) {
        setShowAutoPublishLabel(true);
        localStorage.setItem(LocalStorageKeys.HAS_SEEN_AUTO_PUBLISH, 'true');
      } else {
        setShowAutoPublishLabel(false);
      }
    }
  }, [isOpen]);

  const handleError = (err?: APIError) => {
    if (err) {
      if (err instanceof FieldsAPIError) setFieldErrors(err.fieldErrors);
      else setError(err);
    } else {
      setFieldErrors(undefined);
      setError(false);
    }
  };

  const handleSubmit = (event: any) => {
    event.preventDefault();
    handleError(undefined);

    // Validate that image is required
    if (!pack.image) {
      setFieldErrors({ image: 'Debes seleccionar una imagen' });
      return;
    }

    const normalizedPack = pack.is_surprise
      ? {
          ...pack,
          name:
            !isEdit && !(pack.name || '').trim()
              ? SURPRISE_NAME_PREFIX_WITH_SPACE
              : ensureSurpriseNamePrefix(pack.name),
        }
      : pack;

    // When no_daily_reset is true, send auto_publish_qty and auto_publish_days as null/[] so backend validation passes
    const payload: PackBeingCreated = normalizedPack.no_daily_reset
      ? { ...normalizedPack, auto_publish_qty: null, auto_publish_days: [] }
      : normalizedPack;

    setLoading(true);
    const result = callback(payload);
    result
      .then(() => handleClose(true))
      .catch(handleError)
      .finally(() => setLoading(false));
  };

  const handleClose = (success = false) => {
    if (!isEdit) {
      setPack({});
    }
    handleError(undefined);
    success ? onSuccess() : onClose();
  };

  const handleChange = (event: any) => {
    setPack((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value,
    }));
  };

  const handleNoDailyResetChanged = (event: any) => {
    const checked = !!event.target.checked;
    setPack((prevState) => ({
      ...prevState,
      no_daily_reset: checked,
      ...(checked ? { auto_publish_qty: null, auto_publish_days: [] } : {}),
    }));
    if (checked) {
      setAutoQuantityEnabled(false);
    }
  };

  const handleAutoQuantityEnabledChanged = () => {
    const enabled = !autoQuantityEnabled;
    setAutoQuantityEnabled(enabled);
    if (!enabled) {
      // Remove auto quantity and days if user unchecks the "Publish automatically" checkbox so that it gets updated in the backend
      setPack((prevState) => ({
        ...prevState,
        auto_publish_qty: null,
        auto_publish_days: [],
      }));
    } else {
      // When enabling auto publish, turn off prolonged pack so only one rule applies
      setPack((prevState) => ({
        ...prevState,
        no_daily_reset: false,
        auto_publish_days: WEEKDAYS.map((weekday) => weekday.value),
      }));
    }
  };

  const prolongedDisabledTooltip =
    'A un pack con publicación automática no se le puede quitar el reseteo diario: el stock se repone automáticamente según los días que configures.';
  const autoPublishDisabledTooltip =
    'En un pack sin reseteo diario el stock se mantiene en el tiempo hasta agotar unidades; no es posible configurar publicación automática.';
  const prolongedDisabled = autoQuantityEnabled;
  const autoPublishDisabled = noDailyReset;

  const getFieldError = (field: string) => fieldErrors && fieldErrors[field];

  const isLinked = !!packToEdit?.organization_pack;

  return (
    <Dialog open={isOpen} onClose={() => handleClose(false)} fullWidth>
      <DialogTitle>{isEdit ? 'Editar' : 'Crear'} pack</DialogTitle>
      <DialogContent>
        {isLinked && (
          <>
            <Typography sx={{ mb: 1 }}>
              Este pack está vinculado a un pack compartido. Para editar los campos compartidos, edita el pack compartido o desvincúlalo del
              mismo.
            </Typography>
            <Typography sx={{ mb: 2 }}>Puedes editar la publicación automática debajo.</Typography>
          </>
        )}
        <form noValidate onSubmit={handleSubmit}>
          <BasePackDialogFields
            isOpen={isOpen}
            pack={pack}
            packToEdit={packToEdit}
            handleChange={handleChange}
            getFieldError={getFieldError}
            onClose={onClose}
            isSurprise={!!pack.is_surprise}
            disabled={isLinked}
          />
          {!isEdit && (
            <FormControl className={classes.field} required fullWidth>
              <TextField
                margin='dense'
                id='quantity'
                name='quantity'
                label='Unidades disponibles'
                value={pack['quantity']}
                error={!!getFieldError('quantity')}
                helperText={getFieldError('quantity')}
                onChange={handleChange}
                type='number'
                required
              />
            </FormControl>
          )}
          {pack.is_surprise && (
            <FormHelperText className={classes.field}>
              Este pack se creó como pack {SURPRISE_NAME_PREFIX_LABEL} y debe cumplir el descuento mínimo de packs {SURPRISE_NAME_PREFIX_LABEL} de tu tienda.
            </FormHelperText>
          )}
          {allowsProlongedPacks && (
            <FormControl className={classes.field}>
              <Tooltip title={prolongedDisabled ? prolongedDisabledTooltip : ''} placement='bottom'>
                <span>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={noDailyReset}
                        onChange={handleNoDailyResetChanged}
                        name='no_daily_reset'
                        color='primary'
                        disabled={prolongedDisabled}
                      />
                    }
                    label='Sin reseteo diario'
                    disabled={prolongedDisabled}
                  />
                </span>
              </Tooltip>
              <FormHelperText>
                El pack permanece disponible hasta que se agoten las unidades (no se resetea al cerrar la tienda).
              </FormHelperText>
            </FormControl>
          )}
          <FormControl className={classes.field}>
            <Tooltip title={autoPublishDisabled ? autoPublishDisabledTooltip : ''} placement='bottom'>
              <span>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoQuantityEnabled}
                      onChange={handleAutoQuantityEnabledChanged}
                      name='checkedB'
                      color='primary'
                      disabled={autoPublishDisabled}
                    />
                  }
                  label='Publicación automática'
                  disabled={autoPublishDisabled}
                />
              </span>
            </Tooltip>
            <FormHelperText>Elegir días y número de unidades de este pack para publicar automáticamente.</FormHelperText>
            {showAutoPublishLabel && (
              <TemporalLabel text='¡Ahora puedes configurar packs para que se publiquen automáticamente!' delay={1000} />
            )}
          </FormControl>
          {autoQuantityEnabled && (
            <>
              <FormControl className={classes.field} required fullWidth>
                <TextField
                  margin='dense'
                  id='auto_publish_qty'
                  name='auto_publish_qty'
                  label='Cantidad a publicar cada día'
                  value={pack['auto_publish_qty'] || ''}
                  error={!!getFieldError('auto_publish_qty')}
                  helperText={
                    getFieldError('auto_publish_qty') ||
                    'Cantidad de unidades de este pack que se publicarán automáticamente en los días seleccionados.'
                  }
                  onChange={handleChange}
                  type='number'
                />
              </FormControl>
              <FormControl className={classes.field} key='auto_publish_days' fullWidth>
                <InputLabel>Días en que se publica</InputLabel>
                <Select
                  name='auto_publish_days'
                  multiple
                  value={pack['auto_publish_days'] || []}
                  renderValue={(selected) =>
                    WEEKDAYS.filter((weekday) => selected.includes(weekday.value))
                      .map((weekday) => weekday.name)
                      .join(', ')
                  }
                  onChange={handleChange}
                  label='Días en que se publica'
                >
                  {WEEKDAYS.map((weekday) => (
                    <MenuItem key={weekday.value} value={weekday.value}>
                      <Checkbox color='primary' checked={pack['auto_publish_days']?.includes(weekday.value)} />
                      {weekday.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  No se publicarán unidades los días en los que la tienda no está abierta en Buen Provecho, aunque hayan sido seleccionados.
                  Las unidades se publican el día anterior al cerrar la tienda, para que estén diponibles durante la totalidad de cada día
                  seleccionado.
                </FormHelperText>
              </FormControl>
            </>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)} color='primary'>
          Cancelar
        </Button>
        <Button disabled={loading} variant='contained' onClick={handleSubmit} color='primary'>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
